import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import Axios from 'axios';
import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { usePodApiAccessTokenContext } from './PodApiAccessTokenProvider';
import { useWorkspace } from './WorkspaceProvider';

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      // Keep data fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    },
  },
});

type PaymentsApiHttpClientContextValue = {
  client: AxiosInstance;
  hasWorkspace: boolean;
};

const PaymentsApiHttpClient = createContext<PaymentsApiHttpClientContextValue>({
  client: Axios.create(),
  hasWorkspace: false,
});

/**
 * Provider that sets the authentication token for the Payments API.
 * This should be rendered within the PodApiAccessTokenProvider context.
 */
export const PaymentsApiAuthProvider = ({ children }: PropsWithChildren) => {
  const { workspace } = useWorkspace();
  const { customerApiToken } = usePodApiAccessTokenContext();

  const axiosInstance = useMemo(
    () =>
      Axios.create({
        baseURL: workspace
          ? workspace.pod.customerApiUrl.replace('graphql', '')
          : '',
        headers: {
          'Content-Type': 'application/json',
          'X-Fragment-Workspace': workspace?.id,
          'X-Fragment-Client': 'dashboard',
          Authorization: `Bearer ${customerApiToken}`,
        },
      }),
    [workspace, customerApiToken]
  );

  const contextValue = useMemo(
    () => ({
      client: axiosInstance,
      hasWorkspace: !!workspace,
    }),
    [axiosInstance, workspace]
  );

  return (
    <PaymentsApiHttpClient.Provider value={contextValue}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </PaymentsApiHttpClient.Provider>
  );
};

export function usePaymentsApiHttpClient<T>() {
  const { client } = useContext(PaymentsApiHttpClient);
  return async (options?: AxiosRequestConfig & { body?: string }): Promise<T> =>
    client({ ...options }).then(({ data }) => data);
}

export function usePaymentsApiHttpClientContext() {
  const contextValue = useContext(PaymentsApiHttpClient);
  return contextValue;
}
